import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check, RotateCcw } from "lucide-react";

export default function SignaturePad({ onSave, onClear, width = 500, height = 200 }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      context.strokeStyle = "#1e3a5f";
      context.lineWidth = 2.5;
      context.lineCap = "round";
      context.lineJoin = "round";
      setCtx(context);
      
      // Fill with white background
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      
      // Draw signature line
      context.strokeStyle = "#e5e7eb";
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(40, height - 40);
      context.lineTo(width - 40, height - 40);
      context.stroke();
      
      // Reset stroke style for signature
      context.strokeStyle = "#1e3a5f";
      context.lineWidth = 2.5;
    }
  }, [width, height]);

  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const pos = getPosition(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPosition(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      ctx.closePath();
      setIsDrawing(false);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    
    // Redraw signature line
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(40, height - 40);
    ctx.lineTo(width - 40, height - 40);
    ctx.stroke();
    
    // Reset stroke style
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = 2.5;
    
    setHasSignature(false);
    if (onClear) onClear();
  };

  const saveSignature = () => {
    if (!hasSignature) return;
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    if (onSave) onSave(dataUrl);
  };

  return (
    <div className="space-y-4">
      <div className="relative border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute bottom-2 left-4 text-xs text-slate-400 pointer-events-none">
          Sign above the line
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="text-slate-600"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Clear
        </Button>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {hasSignature ? "✓ Signature captured" : "Draw your signature above"}
          </span>
          <Button
            type="button"
            onClick={saveSignature}
            disabled={!hasSignature}
            className="bg-green-600 hover:bg-green-700"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirm Signature
          </Button>
        </div>
      </div>
    </div>
  );
}
